import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc, onSnapshot } from 'firebase/firestore'

const { db } = initFirebase()

// Celebrity & Historical Twins — the fourth paid Store report
// (reportType == 'celebrityTwins'). Synchronous like Compatibility / Year Ahead:
// no task queue, no PDF. Three figure matches are written in parallel, then a
// synthesis after them; each section lands as it finishes.
//   reportStats/celebrityTwins      — write-time rollup (one read): headline
//     avg/total cost + duration. (No `failed` counter — derive it as
//     count - completed - partial.)
//   report_generations (reportType == 'celebrityTwins') — one row per run with a
//     per-slice cost breakdown + the three picked figure ids (matches[].id).
//   reports (reportType == 'celebrityTwins') — the report itself: the reader's
//     big three, the three matches (photo/score/tier/drivers) and per-section
//     prose. Used for the queue + report detail + the live in-flight view.
//   celebrities — the figure pool (active + draft). Feeds the Figures page.
//   system_logs (functionName == 'generateCelebrityTwinsReport') — error/warn.
//
// Same access path as the other report dashboards (ryomen rules allow authed
// reads; the app signs in anonymously). Collection reads use a single-field
// where('reportType','==','celebrityTwins') — auto-indexed, no composite index —
// then sort/limit client-side, so no row is ever missed.

// Four slices, in the order the backend declares generation.sections: the three
// figure matches run in parallel, the synthesis closes after them.
export const TWINS_SLICES = ['match1', 'match2', 'match3', 'synthesis']

const ERROR_FN = 'generateCelebrityTwinsReport'

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v === 'object' && (v.seconds || v._seconds)) return (v.seconds || v._seconds) * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

function normStats(x) {
  return {
    count: num(x.count),
    completed: num(x.completed),
    partial: num(x.partial),
    // reportStats has no failed counter; the overview derives it.
    repairs: num(x.repairs),
    totalCostUSD: num(x.totalCostUSD),
    totalMs: num(x.totalMs),
    totalInputTokens: num(x.totalInputTokens),
    totalOutputTokens: num(x.totalOutputTokens),
    lastAtMs: toMs(x.lastAt)
  }
}

function mapMatch(m) {
  const b = (m && m.bigThree) || {}
  return {
    slot: num(m.slot),
    celebrityId: m.celebrityId || '',
    name: m.name || '',
    firstName: m.firstName || '',
    era: m.era || '',
    category: m.category || '',
    photo: (m.photo && typeof m.photo === 'object') ? m.photo : null,
    born: m.born || '',
    birthPlace: m.birthPlace || '',
    yearsApart: num(m.yearsApart),
    bornBefore: m.bornBefore === true,
    timeAccuracy: m.timeAccuracy || '',
    sun: b.sun || '',
    moon: b.moon || '',
    rising: b.rising || '',
    score: num(m.score),
    tier: m.tier || '',
    drivers: Array.isArray(m.drivers) ? m.drivers : [],
    houses: Array.isArray(m.houses) ? m.houses : null
  }
}

function mapReport(d) {
  const x = d.data()
  const g = x.generation || {}
  const readyAt = (g.sectionsReadyAt && typeof g.sectionsReadyAt === 'object') ? g.sectionsReadyAt : {}
  const sectionsReadyAtMs = {}
  Object.keys(readyAt).forEach(k => { sectionsReadyAtMs[k] = toMs(readyAt[k]) })
  const u = x.user || {}
  const ub = u.bigThree || {}
  return {
    id: d.id,
    reportId: x.reportId || d.id,
    userId: x.userId || '',
    language: x.language || '',
    title: x.title || '',
    poolSize: num(x.poolSize),
    user: {
      name: u.name || '',
      birthDate: u.birthDate || '',
      timeAccuracy: u.timeAccuracy || '',
      sun: ub.sun || '',
      moon: ub.moon || '',
      rising: ub.rising || '',
      dominant: u.dominant || ''
    },
    matches: Array.isArray(x.matches) ? x.matches.map(mapMatch).sort((a, b) => a.slot - b.slot) : [],
    // Raw per-section prose (match1/2/3/synthesis), rendered in the report detail.
    content: (x.sections && typeof x.sections === 'object') ? x.sections : {},
    state: g.state || '',
    sections: (g.sections && typeof g.sections === 'object') ? g.sections : {},
    sectionsReadyAtMs,
    promptVersion: g.promptVersion || '',
    purchase: x.purchase || null,
    createdAtMs: toMs(x.createdAt) || toMs(x.purchasedAt),
    purchasedAtMs: toMs(x.purchasedAt),
    startedAtMs: toMs(g.startedAt),
    finishedAtMs: toMs(g.finishedAt)
  }
}

function mapGen(d) {
  const x = d.data()
  return {
    id: d.id,
    reportId: x.reportId || '',
    userId: x.userId || '',
    language: x.language || '',
    promptVersion: x.promptVersion || '',
    state: x.state || '',
    partialRepair: x.partialRepair === true,
    poolSize: num(x.poolSize),
    totalMs: num(x.totalMs),
    inputTokens: num(x.inputTokens),
    outputTokens: num(x.outputTokens),
    costUsd: num(x.costUsd),
    slices: (x.slices && typeof x.slices === 'object') ? x.slices : {},
    slicesFailed: Array.isArray(x.slicesFailed) ? x.slicesFailed : [],
    matches: Array.isArray(x.matches) ? x.matches.map(m => ({ id: m.id || '', score: num(m.score), tier: m.tier || '' })) : [],
    createdAtMs: toMs(x.createdAt) || toMs(x.timestamp)
  }
}

function mapCelebrity(d) {
  const x = d.data()
  const birth = x.birth || {}
  const b = (x.analysis && x.analysis.bigThree) || {}
  const chart = x.chart || null
  return {
    id: d.id,
    name: x.name || '',
    era: x.era || '',
    category: x.category || '',
    status: x.status || '',
    description: x.description || '',
    sitelinks: num(x.sitelinks),
    born: birth.date || '',
    birthTime: birth.time || '',
    timeAccuracy: birth.timeAccuracy || '',
    timeSource: birth.timeSource || '',
    birthPlace: birth.place || '',
    gender: x.gender || '',
    occupations: Array.isArray(x.occupations) ? x.occupations : [],
    photo: (x.photo && typeof x.photo === 'object') ? x.photo : null,
    sun: b.sun || '',
    moon: b.moon || '',
    rising: b.rising || '',
    hasChart: !!(chart && chart.planets && chart.planets.length),
    qid: x.qid || ''
  }
}

export class FirebaseCelebrityTwinsReportService {
  // Write-time rollup — the headline numbers, no scan.
  async getStats() {
    const snap = await getDoc(doc(db, 'reportStats', 'celebrityTwins'))
    return snap.exists() ? normStats(snap.data()) : null
  }

  // Per-generation rows, newest first. Single-field where (auto-indexed);
  // sorted client-side so no row is missed.
  async getGenerations(max = 500) {
    const snap = await getDocs(query(collection(db, 'report_generations'), where('reportType', '==', 'celebrityTwins')))
    return snap.docs
      .map(mapGen)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, max)
  }

  // The reports themselves (reader + matches + per-section state), newest first.
  async getReports(max = 200) {
    const snap = await getDocs(query(collection(db, 'reports'), where('reportType', '==', 'celebrityTwins')))
    return snap.docs
      .map(mapReport)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, max)
  }

  // Real-time watch of all twins reports (single-field where, no index), so
  // in-flight runs and their per-section progress update live while watched.
  // Returns the unsubscribe fn. Sorted newest first.
  watchReports(onData, onError) {
    const q = query(collection(db, 'reports'), where('reportType', '==', 'celebrityTwins'))
    return onSnapshot(
      q,
      snap => onData(snap.docs.map(mapReport).sort((a, b) => b.createdAtMs - a.createdAtMs)),
      err => { if (onError) onError(err) }
    )
  }

  // The full figure pool (active + draft) for the Figures page. Sorted by name.
  async getCelebrities(max = 1000) {
    const snap = await getDocs(collection(db, 'celebrities'))
    return snap.docs
      .map(mapCelebrity)
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, max)
  }

  async getErrorLogs(max = 300) {
    const snap = await getDocs(query(collection(db, 'system_logs'), orderBy('createdAt', 'desc'), limit(max)))
    const cutoff = Date.now() - 7 * 86400000
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(x => x.functionName === ERROR_FN && (x.level === 'error' || x.level === 'warn') && toMs(x.createdAt) >= cutoff)
      .map(x => ({
        id: x.id,
        level: x.level,
        message: x.message || '',
        error: x.error || '',
        reportId: (x.metadata && x.metadata.reportId) || '',
        failedSlices: (x.metadata && x.metadata.failedSlices) || null,
        createdAtMs: toMs(x.createdAt)
      }))
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }
}
